import { useTranslation } from '@/contexts/i18n-context';
import { PageContainer } from '@/components/containers/page-container';
import { Section } from '@/components/containers/section';
import { Button } from '@/components/button';
import styles from './payment-success-page.module.css';
import clsx from 'clsx';
import {
  DiagnosisPaymentSvg,
  PrescriptionsPaymentSvg,
  VideoCallPaymentSvg,
} from '@/components/payment/payment-svg';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Navigate, useLocation } from '@/util/react-router-dom';
import z from 'zod';
import { useEncounter } from '@/contexts/encounter-context';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { EncounterStatusIdsType } from '@/api/api.schemas';
import { SuccessCircleSvg } from '@/components/svgs/success-circle';
import { formatPrice } from '@/util/formats';
import { useBranding } from '@/outlets/branding-outlet';
import { PaymentHumanizationBanner } from '@/components/payment/payment-humanization-banner';

const translationBase = 'visit.payment.success';

const successPaymentSchema = z.object({
  amountPaid: z.number(),
  isVideoCallRequired: z.boolean(),
});

export function PaymentSuccessPage() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const { hasCompletedStep } = useVisitNavigation();
  const isMobile = useMediaQuery('--screen-md');
  const location = useLocation();
  const { paymentSuccessCreditCardInfo, humanizationTheme } = useBranding();

  const stateValidation = successPaymentSchema.safeParse(location.state);

  if (!stateValidation.success) {
    const redirectUrl = hasCompletedStep(EncounterStatusIdsType.PreSubmitPaymentInfo)
      ? '/'
      : `/visit/${encounter.id}/payment`;
    return <Navigate to={redirectUrl} replace />;
  }

  const isVideoCallRequired = stateValidation.data.isVideoCallRequired;
  const amountPaid = stateValidation.data.amountPaid;
  const isFreeVisit = stateValidation.data.amountPaid === 0;

  return (
    <PageContainer>
      {!!humanizationTheme && <PaymentHumanizationBanner />}
      <Section className={clsx(styles.successSection, !!humanizationTheme && styles.withBanner)}>
        <SuccessCircleSvg className={styles.icon} />
        <div className={styles.textSection}>
          <h2 className="h3">{t(`${translationBase}.title`)}</h2>
          {paymentSuccessCreditCardInfo ? (
            <p className={clsx(!isMobile && 'body-1')}>{paymentSuccessCreditCardInfo}</p>
          ) : !isFreeVisit ? (
            <p className={clsx(!isMobile && 'body-1')}>
              {t(`${translationBase}.subTitle`, {
                price: formatPrice(amountPaid),
                paymentName: (
                  <span key="payment-name" className="italic">
                    {t(`${translationBase}.paymentName`)}
                  </span>
                ),
              })}
            </p>
          ) : null}
        </div>
        <Button as="a" to="/" rightIcon="arrow_forward" className={styles.continueBtn}>
          {t(`${translationBase}.btnText`)}
        </Button>
        <div className={styles.nextStepsContainer}>
          <span className={clsx('body-1', 'bold')}>{t(`${translationBase}.nextStepsTitle`)}</span>
          <div className={clsx(styles.nextSteps, isVideoCallRequired && styles.hasVideo)}>
            {isVideoCallRequired && (
              <NextStepSection
                text={t(`${translationBase}.videoCall`)}
                icon={<VideoCallPaymentSvg className={styles.img} />}
              />
            )}
            <NextStepSection
              text={t(`${translationBase}.diagnosis`)}
              icon={<PrescriptionsPaymentSvg className={styles.img} />}
            />
            <NextStepSection
              text={t(`${translationBase}.prescriptions`)}
              icon={<DiagnosisPaymentSvg className={styles.img} />}
            />
          </div>
        </div>
      </Section>
    </PageContainer>
  );
}

interface NextStepSectionProps {
  text: string;
  icon: React.ReactNode;
}

function NextStepSection({ text, icon }: NextStepSectionProps) {
  return (
    <div className={styles.nextStepSection}>
      {icon}
      <span className="body-3">{text}</span>
    </div>
  );
}
